import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { SmartSummary } from '../components/SmartSummary';
import { useLanguage } from '../context/LanguageContext';
import { reportsAPI, expensesAPI } from '../lib/api';
import { formatCurrency, formatNumber } from '../lib/utils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Wallet,
  TrendUp,
  Warning,
  CookingPot,
  Package,
  ArrowUp,
  ArrowDown,
  Bell,
  CheckCircle,
  X,
} from '@phosphor-icons/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

function StatCard({ title, value, subtitle, icon: Icon, trend, trendUp, color = 'primary' }) {
  const colorClasses = {
    primary: 'bg-primary/10 text-primary',
    secondary: 'bg-secondary/10 text-secondary',
    accent: 'bg-accent/20 text-accent-foreground',
    destructive: 'bg-destructive/10 text-destructive',
  };

  return (
    <div 
      className="border border-border bg-card p-6 rounded-xl card-hover"
      data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon size={24} weight="duotone" />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-sm font-medium ${trendUp ? 'text-secondary' : 'text-destructive'}`}>
            {trendUp ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
            <span className="font-data">{trend}</span>
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">{title}</p>
        <p className="mt-1 text-2xl lg:text-3xl font-heading font-bold tracking-tight">{value}</p>
        {subtitle && (
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useLanguage();
  const [summary, setSummary] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [topIngredients, setTopIngredients] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reminder, setReminder] = useState(null);
  const [applyingTemplates, setApplyingTemplates] = useState(false);
  const [reminderDismissed, setReminderDismissed] = useState(false);

  useEffect(() => {
    fetchData();
    fetchReminder();
  }, []);

  const fetchData = async () => {
    try {
      const [summaryRes, dailyRes, ingredientsRes, lowStockRes] = await Promise.all([
        reportsAPI.getSummary(),
        reportsAPI.getDaily(30),
        reportsAPI.getTopIngredients(30, 5),
        reportsAPI.getLowStock(),
      ]);
      
      setSummary(summaryRes.data);
      setDailyData(dailyRes.data);
      setTopIngredients(ingredientsRes.data);
      setLowStock(lowStockRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchReminder = async () => {
    try {
      const res = await expensesAPI.getReminder();
      setReminder(res.data);
    } catch (error) {
      console.error('Hatırlatıcı yüklenemedi:', error);
    }
  };

  const handleApplyTemplates = async () => {
    setApplyingTemplates(true);
    try {
      const res = await expensesAPI.autoApplyTemplates();
      toast.success(res.data.message);
      setReminderDismissed(true);
      // Özeti güncelle
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şablonlar uygulanamadı');
    } finally {
      setApplyingTemplates(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        <SmartSummary />
        {/* Expense Reminder Banner */}
        {reminder?.show_reminder && !reminderDismissed && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between" data-testid="expense-reminder">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Bell size={24} className="text-amber-600" weight="fill" />
              </div>
              <div>
                <p className="font-semibold text-amber-800">Gider Hatırlatıcı</p>
                <p className="text-sm text-amber-700">{reminder.message}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={handleApplyTemplates} 
                disabled={applyingTemplates}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="apply-templates-btn"
              >
                <CheckCircle size={18} className="mr-2" />
                {applyingTemplates ? 'Uygulanıyor...' : 'Şablonları Uygula'}
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setReminderDismissed(true)}
                className="text-amber-700 hover:text-amber-800 hover:bg-amber-500/20"
              >
                <X size={20} />
              </Button>
            </div>
          </div>
        )}

        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">
            {t('dashboard.title')}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {t('dashboard.subtitle')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="bento-grid bento-grid-4 stagger-children" data-testid="dashboard-stats">
          <StatCard
            title={t('dashboard.totalStockValue')}
            value={formatCurrency(summary?.total_stock_value || 0)}
            subtitle={`${summary?.product_count || 0} ${t('dashboard.products')}`}
            icon={Wallet}
            color="primary"
          />
          <StatCard
            title={t('dashboard.todayNetProfit')}
            value={formatCurrency(summary?.today_net_profit || summary?.today_profit || 0)}
            subtitle={`${t('dashboard.grossRevenue')}: ${formatCurrency(summary?.today_profit || 0)} | ${summary?.today_sales_count || 0} ${t('dashboard.sales')}`}
            icon={TrendUp}
            trend={summary?.today_net_profit > 0 ? t('dashboard.profit') : t('dashboard.loss')}
            trendUp={summary?.today_net_profit > 0}
            color={(summary?.today_net_profit || 0) >= 0 ? 'secondary' : 'destructive'}
          />
          <StatCard
            title={t('dashboard.lowStock')}
            value={summary?.low_stock_count || 0}
            subtitle={t('dashboard.criticalLevel')}
            icon={Warning}
            color={summary?.low_stock_count > 0 ? 'destructive' : 'accent'}
          />
          <StatCard
            title={t('dashboard.monthNetProfit')}
            value={formatCurrency(summary?.month_net_profit || summary?.month_profit || 0)}
            subtitle={`${t('dashboard.expense')}: ${formatCurrency(summary?.month_expenses || 0)}`}
            icon={CookingPot}
            color={(summary?.month_net_profit || 0) >= 0 ? 'secondary' : 'destructive'}
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profit Chart */}
          <div className="lg:col-span-2 border border-border bg-card p-6 rounded-xl" data-testid="profit-chart">
            <h2 className="font-heading text-xl font-semibold mb-6">{t('dashboard.profitLossChart')}</h2>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value.slice(5)}
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `${value / 1000}K`}
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [formatCurrency(value), '']}
                    labelFormatter={(label) => `Tarih: ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="profit"
                    name="Kar"
                    stroke="hsl(var(--secondary))"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="total_revenue"
                    name="Gelir"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Ingredients */}
          <div className="border border-border bg-card p-6 rounded-xl" data-testid="top-ingredients">
            <h2 className="font-heading text-xl font-semibold mb-6">{t('dashboard.mostUsed')}</h2>
            <div className="space-y-4">
              {topIngredients.length === 0 ? (
                <p className="text-muted-foreground text-sm">{t('dashboard.noDataYet')}</p>
              ) : (
                topIngredients.map((item, index) => (
                  <div key={item.product_id} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.product_name}</p>
                      <p className="text-xs text-muted-foreground">
                        <span className="font-data">{formatNumber(item.total_used)}</span> {item.unit}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Low Stock Alerts */}
        {lowStock.length > 0 && (
          <div className="border border-destructive/30 bg-destructive/5 p-6 rounded-xl" data-testid="low-stock-alerts">
            <div className="flex items-center gap-3 mb-4">
              <Warning size={24} weight="duotone" className="text-destructive" />
              <h2 className="font-heading text-xl font-semibold">Stok Uyarıları</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {lowStock.slice(0, 6).map((item) => (
                <div 
                  key={item.id} 
                  className="flex items-center gap-3 p-3 bg-card rounded-lg border border-border"
                >
                  <Package size={20} weight="duotone" className="text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{item.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Stok: <span className="font-data">{formatNumber(item.total_stock)}</span> {item.unit}
                    </p>
                  </div>
                  <span className={`badge-pill ${item.status === 'critical' ? 'status-critical' : 'status-low'}`}>
                    {item.status === 'critical' ? 'Kritik' : 'Düşük'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Monthly Summary */}
        <div className="border border-border bg-card p-6 rounded-xl" data-testid="monthly-summary">
          <h2 className="font-heading text-xl font-semibold mb-6">Bu Ay Özet</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Toplam Gelir</p>
              <p className="text-2xl font-heading font-bold text-primary font-data">
                {formatCurrency(summary?.month_revenue || 0)}
              </p>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Toplam Kar</p>
              <p className="text-2xl font-heading font-bold text-secondary font-data">
                {formatCurrency(summary?.month_profit || 0)}
              </p>
            </div>
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Satış Adedi</p>
              <p className="text-2xl font-heading font-bold font-data">
                {summary?.month_sales_count || 0}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
